import React, { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Tabs,
  Tab,
  Paper,
  Divider,
  Stack,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  FormGroup,
} from '@mui/material'
import {
  Campaign as CampaignIcon,
  WhatsApp as WhatsAppIcon,
  Email as EmailIcon,
  Sms as SmsIcon,
  Send as SendIcon,
  People as PeopleIcon,
  History as HistoryIcon,
  AutoAwesome as SparklesIcon,
  CheckCircle as CheckCircleIcon,
  LocalOffer as OfferIcon,
  Inventory as StockIcon,
  ContactPhone as LeadIcon,
  Refresh as RefreshIcon,
  Visibility as PreviewIcon,
} from '@mui/icons-material'

type ChipColor = 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'

const CAMPAIGN_STATUS_COLOR: Record<string, ChipColor> = {
  DRAFT: 'default',
  RUNNING: 'info',
  PAUSED: 'warning',
  COMPLETED: 'success',
  FAILED: 'error',
}
import { useSnackbar } from 'notistack'
import apiClient from '../services/api'

interface RemarketingTemplate {
  id: string
  name: string
  category: string
  subject: string
  whatsapp_body: string
  email_html: string
  sms_body?: string
}

interface CampaignHistoryItem {
  id: string
  name: string
  channel: string
  audience_type: string
  template_id: string
  total_recipients: number
  whatsapp_sent: number
  whatsapp_failed: number
  email_sent: number
  email_failed: number
  sms_sent: number
  sms_failed: number
  created_by: string
  created_at: string
  status: string
}

const RemarketingPage: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar()
  const [tabIndex, setTabIndex] = useState<number>(0)

  // Campaign Form State
  const [campaignName, setCampaignName] = useState<string>('')
  const [channel, setChannel] = useState<'both' | 'whatsapp' | 'email' | 'sms'>('both')
  const [audienceType, setAudienceType] = useState<string>('all_leads')
  const [selectedLeadStatuses, setSelectedLeadStatuses] = useState<string[]>(['new', 'contacted'])
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('new_goods_arrived')
  const [customSubject, setCustomSubject] = useState<string>('')
  const [customWhatsAppBody, setCustomWhatsAppBody] = useState<string>('')
  const [customEmailHtml, setCustomEmailHtml] = useState<string>('')
  const [customSmsBody, setCustomSmsBody] = useState<string>('')

  // Audience Preview & Data
  const [templates, setTemplates] = useState<RemarketingTemplate[]>([])
  const [audiencePreview, setAudiencePreview] = useState<{
    total_recipients: number
    whatsapp_reachable: number
    email_reachable: number
    sms_reachable: number
    leads_count: number
    customers_count: number
  }>({
    total_recipients: 0,
    whatsapp_reachable: 0,
    email_reachable: 0,
    sms_reachable: 0,
    leads_count: 0,
    customers_count: 0,
  })
  const [previewLoading, setPreviewLoading] = useState<boolean>(false)
  const [sending, setSending] = useState<boolean>(false)

  // History State
  const [campaigns, setCampaigns] = useState<CampaignHistoryItem[]>([])
  const [historyLoading, setHistoryLoading] = useState<boolean>(false)

  // Load Templates & History
  useEffect(() => {
    loadTemplates()
    loadCampaignHistory()
  }, [])

  // Update audience preview whenever audience criteria change
  useEffect(() => {
    fetchAudiencePreview()
  }, [audienceType, selectedLeadStatuses])

  // Periodic polling for in-flight campaigns
  useEffect(() => {
    const hasActiveCampaigns = campaigns.some((c) => c.status === 'RUNNING' || c.status === 'DRAFT')
    if (!hasActiveCampaigns) return

    const interval = setInterval(() => {
      loadCampaignHistory()
    }, 4000)

    return () => clearInterval(interval)
  }, [campaigns])

  const loadTemplates = async () => {
    try {
      const res = await apiClient.get('/admin/remarketing/templates')
      if (res.data?.templates) {
        setTemplates(res.data.templates)
        const defTpl = res.data.templates.find((t: RemarketingTemplate) => t.id === 'new_goods_arrived')
        if (defTpl) {
          setCustomSubject(defTpl.subject)
          setCustomWhatsAppBody(defTpl.whatsapp_body)
          setCustomEmailHtml(defTpl.email_html)
          setCustomSmsBody(defTpl.sms_body || '')
        }
      }
    } catch (err) {
      console.error('Failed to load templates:', err)
    }
  }

  const loadCampaignHistory = async () => {
    setHistoryLoading(true)
    try {
      const res = await apiClient.get('/admin/remarketing/campaigns')
      setCampaigns(res.data?.items || [])
    } catch (err) {
      console.error('Failed to load campaign history:', err)
    } finally {
      setHistoryLoading(false)
    }
  }

  const fetchAudiencePreview = async () => {
    setPreviewLoading(true)
    try {
      const res = await apiClient.post('/admin/remarketing/audience-preview', {
        audience_type: audienceType,
        lead_statuses: audienceType === 'leads_by_status' ? selectedLeadStatuses : undefined,
      })
      setAudiencePreview(res.data)
    } catch (err) {
      console.error('Failed to preview audience:', err)
    } finally {
      setPreviewLoading(false)
    }
  }

  const handleTemplateChange = (tplId: string) => {
    setSelectedTemplateId(tplId)
    const tpl = templates.find((t) => t.id === tplId)
    if (tpl) {
      setCustomSubject(tpl.subject)
      setCustomWhatsAppBody(tpl.whatsapp_body)
      setCustomEmailHtml(tpl.email_html)
      setCustomSmsBody(tpl.sms_body || '')
    }
  }

  const handleToggleLeadStatus = (statusVal: string) => {
    setSelectedLeadStatuses((prev) =>
      prev.includes(statusVal) ? prev.filter((s) => s !== statusVal) : [...prev, statusVal]
    )
  }

  const handleSendCampaign = async () => {
    if (!campaignName.trim()) {
      enqueueSnackbar('Please enter a Campaign Name', { variant: 'warning' })
      return
    }

    if (audiencePreview.total_recipients === 0) {
      enqueueSnackbar('Selected audience contains 0 recipients', { variant: 'warning' })
      return
    }

    setSending(true)
    try {
      const payload = {
        campaign_name: campaignName.trim(),
        channel,
        audience_type: audienceType,
        template_id: selectedTemplateId,
        custom_subject: customSubject,
        custom_whatsapp_body: customWhatsAppBody,
        custom_email_html: customEmailHtml,
        custom_sms_body: customSmsBody,
        lead_statuses: audienceType === 'leads_by_status' ? selectedLeadStatuses : undefined,
      }

      const res = await apiClient.post('/admin/remarketing/campaigns', payload)
      enqueueSnackbar(
        `Campaign "${campaignName}" queued! Sending to ${res.data.total_recipients} recipients in the background — track progress in Campaign History.`,
        { variant: 'success' }
      )

      // Reset form and refresh history
      setCampaignName('')
      loadCampaignHistory()
      setTabIndex(1) // Switch to history tab
    } catch (err: any) {
      console.error('Failed to send campaign:', err)
      enqueueSnackbar(err?.response?.data?.detail || 'Failed to dispatch remarketing campaign', {
        variant: 'error',
      })
    } finally {
      setSending(false)
    }
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 2,
                bgcolor: 'rgba(220, 38, 38, 0.1)',
                color: '#dc2626',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <CampaignIcon fontSize="medium" />
            </Box>
            <Box>
              <Typography variant="h5" fontWeight={800} color="text.primary">
                Remarketing & Lead Follow-ups
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Connect and send automated follow-ups to Customers & Leads via WhatsApp and Email
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Main Tabs Navigation */}
      <Card sx={{ borderRadius: 2, mb: 3 }}>
        <Tabs
          value={tabIndex}
          onChange={(_, val) => setTabIndex(val)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
        >
          <Tab
            icon={<SparklesIcon />}
            iconPosition="start"
            label="New Follow-up Campaign"
            sx={{ fontWeight: 700, textTransform: 'none' }}
          />
          <Tab
            icon={<HistoryIcon />}
            iconPosition="start"
            label="Campaign History & Logs"
            sx={{ fontWeight: 700, textTransform: 'none' }}
          />
          <Tab
            icon={<OfferIcon />}
            iconPosition="start"
            label="Templates Library"
            sx={{ fontWeight: 700, textTransform: 'none' }}
          />
        </Tabs>
      </Card>

      {/* TAB 0: Campaign Creator */}
      {tabIndex === 0 && (
        <Grid container spacing={3}>
          {/* Left Column: Configuration & Message Composer */}
          <Grid item xs={12} lg={8}>
            <Card sx={{ borderRadius: 2, p: 3, mb: 3 }}>
              <Typography variant="h6" fontWeight={700} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <SendIcon color="primary" fontSize="small" /> 1. Campaign Details & Channels
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={8}>
                  <TextField
                    fullWidth
                    label="Campaign Name"
                    placeholder="e.g. Fresh Goods Arrival — March 2026 Batch"
                    value={campaignName}
                    onChange={(e) => setCampaignName(e.target.value)}
                    size="small"
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Dispatch Channel</InputLabel>
                    <Select
                      value={channel}
                      label="Dispatch Channel"
                      onChange={(e) => setChannel(e.target.value as any)}
                    >
                      <MenuItem value="both">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <WhatsAppIcon sx={{ color: '#25D366', fontSize: 18 }} />
                          <EmailIcon sx={{ color: '#2563EB', fontSize: 18 }} />
                          <Typography variant="body2" fontWeight={600}>WhatsApp + Email</Typography>
                        </Box>
                      </MenuItem>
                      <MenuItem value="whatsapp">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <WhatsAppIcon sx={{ color: '#25D366', fontSize: 18 }} />
                          <Typography variant="body2">WhatsApp Only</Typography>
                        </Box>
                      </MenuItem>
                      <MenuItem value="email">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <EmailIcon sx={{ color: '#2563EB', fontSize: 18 }} />
                          <Typography variant="body2">Email Only</Typography>
                        </Box>
                      </MenuItem>
                      <MenuItem value="sms">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <SmsIcon sx={{ color: '#f59e0b', fontSize: 18 }} />
                          <Typography variant="body2">SMS Only</Typography>
                        </Box>
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />

              {/* Target Audience */}
              <Typography variant="h6" fontWeight={700} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <PeopleIcon color="primary" fontSize="small" /> 2. Target Audience Selection
              </Typography>

              <FormControl component="fieldset" sx={{ mb: 2 }}>
                <RadioGroup
                  row
                  value={audienceType}
                  onChange={(e) => setAudienceType(e.target.value)}
                >
                  <FormControlLabel value="all_leads" control={<Radio />} label="All Active Leads" />
                  <FormControlLabel value="leads_by_status" control={<Radio />} label="Leads by Status" />
                  <FormControlLabel value="all_customers" control={<Radio />} label="All Customers" />
                  <FormControlLabel value="all" control={<Radio />} label="Leads + Customers (All)" />
                </RadioGroup>
              </FormControl>

              {audienceType === 'leads_by_status' && (
                <Box sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: 2, border: 1, borderColor: 'divider', mb: 2 }}>
                  <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    Filter Lead Statuses:
                  </Typography>
                  <FormGroup row>
                    {['new', 'contacted', 'qualified', 'proposal_sent', 'negotiation'].map((statusKey) => (
                      <FormControlLabel
                        key={statusKey}
                        control={
                          <Checkbox
                            checked={selectedLeadStatuses.includes(statusKey)}
                            onChange={() => handleToggleLeadStatus(statusKey)}
                            size="small"
                          />
                        }
                        label={<Typography variant="body2">{statusKey.replace('_', ' ').toUpperCase()}</Typography>}
                      />
                    ))}
                  </FormGroup>
                </Box>
              )}

              <Divider sx={{ my: 3 }} />

              {/* Template & Message Content */}
              <Typography variant="h6" fontWeight={700} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <SparklesIcon color="primary" fontSize="small" /> 3. Message Template & Personalization
              </Typography>

              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel>Pre-built High Converting Template</InputLabel>
                <Select
                  value={selectedTemplateId}
                  label="Pre-built High Converting Template"
                  onChange={(e) => handleTemplateChange(e.target.value)}
                >
                  {templates.map((tpl) => (
                    <MenuItem key={tpl.id} value={tpl.id}>
                      <strong>{tpl.name}</strong> &nbsp;
                      <Typography variant="caption" color="text.secondary">
                        ({tpl.category})
                      </Typography>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {(channel === 'email' || channel === 'both') && (
                <TextField
                  fullWidth
                  label="Email Subject Line"
                  value={customSubject}
                  onChange={(e) => setCustomSubject(e.target.value)}
                  size="small"
                  sx={{ mb: 2 }}
                />
              )}

              {(channel === 'whatsapp' || channel === 'both') && (
                <TextField
                  fullWidth
                  label="WhatsApp Message Body"
                  multiline
                  rows={6}
                  value={customWhatsAppBody}
                  onChange={(e) => setCustomWhatsAppBody(e.target.value)}
                  helperText="Supports markdown (*bold*, _italic_) and merge tags: {{name}}, {{company}}"
                  sx={{ mb: 2 }}
                />
              )}

              {(channel === 'email' || channel === 'both') && (
                <TextField
                  fullWidth
                  label="Email HTML Content"
                  multiline
                  rows={5}
                  value={customEmailHtml}
                  onChange={(e) => setCustomEmailHtml(e.target.value)}
                  helperText="HTML supported. Available merge tags: {{name}}, {{company}}"
                />
              )}

              {channel === 'sms' && (
                <TextField
                  fullWidth
                  label="SMS Message Body"
                  multiline
                  rows={3}
                  value={customSmsBody}
                  onChange={(e) => setCustomSmsBody(e.target.value)}
                  helperText="Plain text only, keep it short. Merge tags: {{name}}, {{company}}"
                />
              )}
            </Card>
          </Grid>

          {/* Right Column: Audience Counter & Action */}
          <Grid item xs={12} lg={4}>
            <Card sx={{ borderRadius: 2, p: 3, mb: 3, bgcolor: '#f8fafc', border: 1, borderColor: 'divider' }}>
              <Typography variant="h6" fontWeight={700} gutterBottom>
                Audience Reach Summary
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Calculated in real-time from active leads and customers
              </Typography>

              <Box sx={{ mt: 2.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Paper sx={{ p: 2, borderRadius: 2, bgcolor: 'white', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>TOTAL RECIPIENTS</Typography>
                    <Typography variant="h4" fontWeight={800} color="primary.main">
                      {previewLoading ? <CircularProgress size={24} /> : audiencePreview.total_recipients}
                    </Typography>
                  </Box>
                  <PeopleIcon sx={{ fontSize: 36, color: 'primary.light' }} />
                </Paper>

                {(channel === 'whatsapp' || channel === 'both') && (
                  <Paper sx={{ p: 2, borderRadius: 2, bgcolor: 'white', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>WHATSAPP READY</Typography>
                      <Typography variant="h5" fontWeight={700} sx={{ color: '#16a34a' }}>
                        {previewLoading ? '...' : `${audiencePreview.whatsapp_reachable} phones`}
                      </Typography>
                    </Box>
                    <WhatsAppIcon sx={{ fontSize: 32, color: '#22c55e' }} />
                  </Paper>
                )}

                {(channel === 'email' || channel === 'both') && (
                  <Paper sx={{ p: 2, borderRadius: 2, bgcolor: 'white', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>EMAIL READY</Typography>
                      <Typography variant="h5" fontWeight={700} sx={{ color: '#2563eb' }}>
                        {previewLoading ? '...' : `${audiencePreview.email_reachable} emails`}
                      </Typography>
                    </Box>
                    <EmailIcon sx={{ fontSize: 32, color: '#3b82f6' }} />
                  </Paper>
                )}

                {channel === 'sms' && (
                  <Paper sx={{ p: 2, borderRadius: 2, bgcolor: 'white', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>SMS READY</Typography>
                      <Typography variant="h5" fontWeight={700} sx={{ color: '#f59e0b' }}>
                        {previewLoading ? '...' : `${audiencePreview.sms_reachable} phones`}
                      </Typography>
                    </Box>
                    <SmsIcon sx={{ fontSize: 32, color: '#fbbf24' }} />
                  </Paper>
                )}

                <Alert severity="info" sx={{ fontSize: '0.78rem' }}>
                  Messages will be personalized with the customer or lead's direct name and business details.
                </Alert>

                <Button
                  variant="contained"
                  color="error"
                  size="large"
                  fullWidth
                  onClick={handleSendCampaign}
                  disabled={sending || audiencePreview.total_recipients === 0}
                  startIcon={sending ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                  sx={{ py: 1.5, fontWeight: 800, fontSize: '0.95rem', borderRadius: 2, mt: 1 }}
                >
                  {sending ? 'Dispatching Campaign...' : '🚀 Send Follow-up Campaign Now'}
                </Button>
              </Box>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* TAB 1: Campaign History */}
      {tabIndex === 1 && (
        <Card sx={{ borderRadius: 2, p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" fontWeight={700}>
              Executed Campaigns & Follow-up Logs
            </Typography>
            <Button
              startIcon={<RefreshIcon />}
              onClick={loadCampaignHistory}
              size="small"
              variant="outlined"
            >
              Refresh Logs
            </Button>
          </Box>

          {historyLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : campaigns.length === 0 ? (
            <Alert severity="info" sx={{ my: 2 }}>
              No remarketing campaigns have been sent yet. Click "New Follow-up Campaign" to launch your first broadcast!
            </Alert>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead sx={{ bgcolor: '#f8fafc' }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Campaign Name</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Channel</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Audience</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Recipients</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>WhatsApp Sent</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Email Sent</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>SMS Sent</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Dispatched On</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {campaigns.map((c) => (
                    <TableRow key={c.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={700}>
                          {c.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          By: {c.created_by}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={c.channel.toUpperCase()}
                          size="small"
                          color={c.channel === 'whatsapp' ? 'success' : c.channel === 'email' ? 'primary' : c.channel === 'sms' ? 'warning' : 'secondary'}
                          sx={{ fontWeight: 700, fontSize: '0.7rem' }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{c.audience_type.replace('_', ' ')}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={700}>
                          {c.total_recipients}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="success.main" fontWeight={600}>
                          {c.whatsapp_sent}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="primary.main" fontWeight={600}>
                          {c.email_sent}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: '#f59e0b' }} fontWeight={600}>
                          {c.sms_sent}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">
                          {new Date(c.created_at).toLocaleString()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={c.status}
                          size="small"
                          color={CAMPAIGN_STATUS_COLOR[c.status] || 'default'}
                          variant="outlined"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Card>
      )}

      {/* TAB 2: Templates Gallery */}
      {tabIndex === 2 && (
        <Grid container spacing={3}>
          {templates.map((tpl) => (
            <Grid item xs={12} md={4} key={tpl.id}>
              <Card sx={{ borderRadius: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Chip label={tpl.category} size="small" color="primary" variant="outlined" />
                  </Box>
                  <Typography variant="h6" fontWeight={700} gutterBottom>
                    {tpl.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                    Subject: <strong>{tpl.subject}</strong>
                  </Typography>

                  <Paper variant="outlined" sx={{ p: 1.5, bgcolor: '#f8fafc', borderRadius: 1.5, mb: 2, maxHeight: 180, overflowY: 'auto' }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ display: 'block', mb: 0.5 }}>
                      WhatsApp Message Preview:
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: '0.78rem', whiteSpace: 'pre-line' }}>
                      {tpl.whatsapp_body}
                    </Typography>
                  </Paper>

                  {tpl.sms_body && (
                    <Paper variant="outlined" sx={{ p: 1.5, bgcolor: '#fffbeb', borderRadius: 1.5, mb: 2, maxHeight: 120, overflowY: 'auto' }}>
                      <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ display: 'block', mb: 0.5 }}>
                        SMS Preview:
                      </Typography>
                      <Typography variant="body2" sx={{ fontSize: '0.78rem', whiteSpace: 'pre-line' }}>
                        {tpl.sms_body}
                      </Typography>
                    </Paper>
                  )}
                </CardContent>

                <Box sx={{ p: 2, pt: 0 }}>
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={<SparklesIcon />}
                    onClick={() => {
                      handleTemplateChange(tpl.id)
                      setTabIndex(0)
                      enqueueSnackbar(`Loaded template "${tpl.name}" into Campaign Wizard`, { variant: 'info' })
                    }}
                    sx={{ textTransform: 'none', fontWeight: 600 }}
                  >
                    Use this Template
                  </Button>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  )
}

export default RemarketingPage
